import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { InputNumber, Switch } from 'antd'
import {
  FormCard,
  FormItem,
  MultiSelect,
  Input,
  SelectAddOption,
  GroupedUsersMultiSelect,
} from '@lmc/ui-kit'
import { styled } from 'styled-components'
import {
  getRequiredRule,
  getTeamMembersClockInsRule,
} from 'screens/Rostering/utils'
import { cleanDecimalValues } from '../../formComponents/utils'
import { VisitType } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'

type InstanceTypeValue = {
  id: string
  name: string
}

type InstanceFormCardProps = {
  form: any
  formData: any
  visitTypes?: VisitType[]
  users?: any
  allSkills?: Array<{ id: string; name: string }>
  flagsRosteringEditingLimitations?: boolean
  setFormData: (data: any) => void
  isSubmitting?: boolean
}

const InstanceFormCard: FC<InstanceFormCardProps> = ({
  form,
  formData,
  visitTypes,
  users,
  allSkills,
  flagsRosteringEditingLimitations,
  setFormData,
  isSubmitting = false,
}) => {
  const { t } = useTranslation()
  const [showOptionalDetails, setShowOptionalDetails] = useState(false)
  const [instanceTypeValue, setInstanceTypeValue] = useState<
    InstanceTypeValue | undefined
  >(undefined)
  const [showValidation, setShowValidation] = useState(false)

  const instanceTypeOptions =
    visitTypes?.map((item) => ({
      value: {
        // @ts-ignore
        id: item._id || item.id,
        name: item.name,
      },
      label: item.name,
    })) || []

  const onSkillsChange = (selectedSkills: string[]) => {
    const formattedSkills = selectedSkills.map((skillId) => ({
      skillId,
      skillSupplyNumber: 1,
    }))

    form.setFieldValue('skills', formattedSkills)
    setFormData((prev: any) => ({
      ...prev,
      skills: formattedSkills,
    }))
  }

  useEffect(() => {
    if (formData?.instanceTypeId && !instanceTypeValue) {
      const foundType = instanceTypeOptions.find(
        (option) => option.value.id === formData.instanceTypeId,
      )
      if (foundType) {
        setInstanceTypeValue(foundType.value)
      }
    }
  }, [formData?.instanceTypeId, instanceTypeOptions, instanceTypeValue])

  useEffect(() => {
    if (isSubmitting) {
      setShowValidation(true)
    }
  }, [isSubmitting])

  useEffect(() => {
    if (instanceTypeValue) {
      setShowValidation(false)
    }
  }, [instanceTypeValue])

  const handleInstanceTypeChange = (value: InstanceTypeValue) => {
    setInstanceTypeValue(value)
    const isNewInstanceType = !instanceTypeOptions.some(
      (option) =>
        option.value.id === value.id && option.value.name === value.name,
    )

    if (isNewInstanceType) {
      setFormData((prev: any) => ({
        ...prev,
        newInstanceType: {
          name: value.name,
        },
        instanceTypeId: undefined,
      }))
    } else if (value && value.id) {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: value.id,
        newInstanceType: undefined,
      }))

      form?.setFieldValue('instanceTypeId', value.id)
    } else {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: undefined,
        newInstanceType: undefined,
      }))
    }
  }

  const handleOptionalDetailsToggle = (checked: boolean) => {
    setShowOptionalDetails(checked)

    if (!checked) {
      form.setFieldsValue({
        skills: undefined,
        slotNotes: undefined,
      })

      setFormData((prev: any) => ({
        ...prev,
        skills: undefined,
        slotNotes: undefined,
      }))
    }
  }

  return (
    <FormCard level={2} padding="16px">
      <FormItem
        label={
          <MandatoryText>
            <RedText>*</RedText>
            <Text>{t('TYPE_OF_VISIT')}</Text>
          </MandatoryText>
        }
        rules={[getRequiredRule(t('TYPE_OF_VISIT'))]}
        validateStatus={showValidation && !instanceTypeValue ? 'error' : ''}
        help={
          showValidation && !instanceTypeValue
            ? t('TYPE_OF_VISIT') + ' is required'
            : ''
        }
      >
        <SelectAddOption<InstanceTypeValue>
          options={instanceTypeOptions}
          selectedValue={instanceTypeValue}
          setSelectedValue={handleInstanceTypeChange}
          valueKey="id"
          fields={[
            {
              key: 'name',
              placeholder: t('TYPE_NEW_VISIT_TYPE'),
              type: 'text',
              size: 100,
            },
          ]}
        />
      </FormItem>

      <FormItem name="instanceTypeId" hidden>
        <input type="hidden" />
      </FormItem>

      <FormItem
        name="title"
        label={<Text>{t('TITLE')}</Text>}
        rules={[getRequiredRule(t('TITLE'))]}
      >
        <Input />
      </FormItem>

      <InlineFormItem>
        <NumberSupplyFormItem
          labelCol={labelColStyle}
          labelAlign="left"
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
          rules={[
            getRequiredRule(t('TEAM_MEMBERS_REQUIRED')),
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    ...getTeamMembersClockInsRule(
                      formData?.status,
                      formData?.clockIns,
                    ),
                    validateTrigger: 'onSubmit',
                  },
                ]
              : []),
          ]}
        >
          <InputNumber
            style={{ width: 72 }}
            min={1}
            max={50}
            type="number"
            formatter={cleanDecimalValues}
          />
        </NumberSupplyFormItem>
        <TeamMembersFormItem name="teamMembers">
          <GroupedUsersMultiSelect
            users={users}
            title={t('SELECT_TEAM_MEMBERS')}
            values={formData?.teamMembers || []}
            hasClear
            hasSearch
            hasSelectAll
            placeholder
            maxTagCount={0}
            suffixIcon={
              <Text>
                {formData?.teamMembers?.length ?? 0}/
                {formData?.numberSupply ?? 0}
              </Text>
            }
          />
        </TeamMembersFormItem>
      </InlineFormItem>
      <FormItem>
        <FlexContainer>
          <Switch
            checked={showOptionalDetails}
            onChange={handleOptionalDetailsToggle}
          />
          <StyledText>{t('OPTIONAL_DETAILS')}</StyledText>
        </FlexContainer>
      </FormItem>
      {showOptionalDetails && (
        <>
          <FormItem name="skills" label={<Text>{t('SKILLS_REQUIRED')}</Text>}>
            <MultiSelect
              title={t('SELECT_SKILLS_SHORT')}
              options={allSkills?.map((skill) => ({
                value: skill?.id,
                label: skill?.name,
              }))}
              hasClear
              hasSearch
              hasSelectAll
              values={
                formData?.skills?.map((skill: any) =>
                  typeof skill === 'string' ? skill : skill.skillId,
                ) || []
              }
              onChange={onSkillsChange}
            />
          </FormItem>
          <FormItem name="slotNotes" label={<Text>{t('DESCRIPTION')}</Text>}>
            <Input />
          </FormItem>
        </>
      )}
    </FormCard>
  )
}

const labelColStyle = {
  style: {
    width: 200,
  },
}

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NumberSupplyFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color4};
  }
  width: 200px;
`

const TeamMembersFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  flex: 1;
  min-width: 0;
`

const InlineFormItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 130px;
`
const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const MandatoryText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const RedText = styled.span`
  color: red;
`

export default InstanceFormCard
